"use client";

import { c as _c } from "react/compiler-runtime";
import { Box, type BoxProps, UnstyledButton } from "@mantine/core";
import * as React from "react";
import { Tooltip } from "@/shared/ui";
import { cn } from "@/lib/utils";
import { useSidebarLayout } from "./sidebar-layout-context";
import classes from "./sidebar-layout.module.scss";
import { type ButtonHTMLAttributes, type ReactNode } from "react";
export function SidebarInset(t0) {
  const $ = _c(11);
  let children;
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
  }
  let t1;
  if ($[4] !== className) {
    t1 = cn(classes.inset, className);
    $[4] = className;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <Box className={classes.mobileHeaderSpacer} aria-hidden={true} data-slot="sidebar-mobile-header-spacer" />;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== children || $[8] !== props || $[9] !== t1) {
    t3 = <Box component="main" data-slot="sidebar-inset" className={t1} {...props}>{t2}{children}</Box>;
    $[7] = children;
    $[8] = props;
    $[9] = t1;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  return t3;
}
export function SidebarGroup(t0) {
  const $ = _c(10);
  let children;
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
  }
  let t1;
  if ($[4] !== className) {
    t1 = cn(classes.group, className);
    $[4] = className;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== children || $[7] !== props || $[8] !== t1) {
    t2 = <Box data-slot="sidebar-group" data-sidebar="group" className={t1} {...props}>{children}</Box>;
    $[6] = children;
    $[7] = props;
    $[8] = t1;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  return t2;
}
export function SidebarMenu(t0) {
  const $ = _c(10);
  let children;
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
  }
  let t1;
  if ($[4] !== className) {
    t1 = cn(classes.menu, className);
    $[4] = className;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== children || $[7] !== props || $[8] !== t1) {
    t2 = <Box component="ul" data-slot="sidebar-menu" data-sidebar="menu" className={t1} {...props}>{children}</Box>;
    $[6] = children;
    $[7] = props;
    $[8] = t1;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  return t2;
}
export function SidebarMenuItem(t0) {
  const $ = _c(10);
  let children;
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
  }
  let t1;
  if ($[4] !== className) {
    t1 = cn(classes.menuItem, className);
    $[4] = className;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== children || $[7] !== props || $[8] !== t1) {
    t2 = <Box component="li" data-slot="sidebar-menu-item" data-sidebar="menu-item" className={t1} {...props}>{children}</Box>;
    $[6] = children;
    $[7] = props;
    $[8] = t1;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  return t2;
}
export type SidebarMenuButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  asChild?: boolean;
  isActive?: boolean;
  tooltip?: string;
};
export function SidebarMenuButton(t0) {
  const $ = _c(23);
  let children;
  let className;
  let props;
  let t1;
  let t2;
  let tooltip;
  if ($[0] !== t0) {
    ({
      asChild: t1,
      isActive: t2,
      tooltip,
      className,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
    $[4] = t1;
    $[5] = t2;
    $[6] = tooltip;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
    t1 = $[4];
    t2 = $[5];
    tooltip = $[6];
  }
  const asChild = t1 === undefined ? false : t1;
  const isActive = t2 === undefined ? false : t2;
  const {
    isMobile,
    showCollapsedVisual,
    phase
  } = useSidebarLayout();
  const isMenuFadingOut = phase === "menu-out";
  const showTooltip = Boolean(tooltip) && showCollapsedVisual && phase !== "icons-in";
  let button;
  if ($[7] !== asChild || $[8] !== children || $[9] !== className || $[10] !== isActive || $[11] !== isMenuFadingOut || $[12] !== isMobile || $[13] !== props || $[14] !== showCollapsedVisual) {
    const buttonClassName = cn(classes.menuButton, isMenuFadingOut && !isMobile && classes.menuButtonClosing, showCollapsedVisual && classes.menuButtonCollapsed, className);
    const sharedProps = {
      "data-slot": "sidebar-menu-button",
      "data-sidebar": "menu-button",
      "data-active": isActive,
      className: buttonClassName
    } as const;
    if (asChild && React.isValidElement(children)) {
      button = React.cloneElement(children, {
        ...props,
        ...sharedProps,
        className: cn(buttonClassName, children.props.className)
      } as Partial<typeof children.props>);
    } else {
      let t3;
      if ($[16] !== children || $[17] !== props || $[18] !== sharedProps) {
        t3 = <UnstyledButton {...sharedProps} {...props}>{children}</UnstyledButton>;
        $[16] = children;
        $[17] = props;
        $[18] = sharedProps;
        $[19] = t3;
      } else {
        t3 = $[19];
      }
      button = t3;
    }
    $[7] = asChild;
    $[8] = children;
    $[9] = className;
    $[10] = isActive;
    $[11] = isMenuFadingOut;
    $[12] = isMobile;
    $[13] = props;
    $[14] = showCollapsedVisual;
    $[15] = button;
  } else {
    button = $[15];
  }
  if (!showTooltip || !tooltip) {
    return button;
  }
  let t3;
  if ($[20] !== button || $[21] !== tooltip) {
    t3 = <Tooltip content={tooltip} side="right">{button}</Tooltip>;
    $[20] = button;
    $[21] = tooltip;
    $[22] = t3;
  } else {
    t3 = $[22];
  }
  return t3;
}